import { HttpClient, HttpHandler, HttpXhrBackend } from '@angular/common/http';
import { Injector, type Type, enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  type MsalGuardConfiguration,
  type MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';

import { AppModule } from './app/app.module';
import { ConfigurationService } from './app/services/configuration.service';
import { TestLoginService } from './app/services/test-login.service';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

const injector = Injector.create({
  providers: [
    { provide: HttpClient, deps: [HttpHandler] },
    { provide: HttpHandler, useValue: new HttpXhrBackend({ build: () => new XMLHttpRequest() }) },
    { provide: ConfigurationService, deps: [HttpClient] },
  ],
});

const configurationService = injector.get(ConfigurationService as Type<ConfigurationService>);

console.log('Initializing configuration service');

configurationService.initializeConfiguration().then(async () => {
  const config = configurationService.getConfig();

  // Check if we're in test mode
  const isTestMode = environment.testMode || localStorage.getItem('cypress-test-user') !== null;

  if (isTestMode) {
    console.log('[Main] Running in test mode - skipping MSAL initialization');

    // Initialize test login service (but don't auto-login)
    const testLoginService = new TestLoginService(
      new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() })),
      configurationService,
    );

    // Don't auto-login - let the user login manually
    // await testLoginService.initializeTestLogin();

    // Bootstrap with test mode providers
    platformBrowserDynamic([
      { provide: ConfigurationService, useValue: configurationService },
      { provide: TestLoginService, useValue: testLoginService },
    ])
      .bootstrapModule(AppModule)
      .catch(err => console.error(err));
  } else {
    // Normal MSAL flow
    const msalConfig = config.msalConfig;

    const msalInstance = new PublicClientApplication({
      auth: {
        clientId: msalConfig.clientId,
        authority: msalConfig.authority,
        redirectUri: msalConfig.redirectUri,
        // navigateToLoginRequestUrl: true
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: isIE, // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v1-docs/ie-support.md
      },
    });

    await msalInstance.initialize();

    const guardConfig: MsalGuardConfiguration = {
      interactionType: InteractionType.Redirect,
    };

    const apiServiceVersionUrlBase = config.apiServiceEndpoint + 'v1/';
    const apiScope = config.apiScope;

    const interceptorConfig: MsalInterceptorConfiguration = {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map([
        // protectedResources go here. MSAL adds appropriate token to outbound API calls
        [`${apiServiceVersionUrlBase}Barcode/GetBarcodesByIdentifier*`, null],
        [apiServiceVersionUrlBase, [apiScope]],
      ]),
    };

    platformBrowserDynamic([
      { provide: ConfigurationService, useValue: configurationService },
      { provide: MSAL_INSTANCE, useValue: msalInstance },
      { provide: MSAL_INTERCEPTOR_CONFIG, useValue: interceptorConfig },
      { provide: MSAL_GUARD_CONFIG, useValue: guardConfig },
    ])
      .bootstrapModule(AppModule)
      .catch(err => console.error(err));
  }
});
